import { Box, styled, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useProductStocksSWR from '../../hooks/products/useProductStocks';
import { Product, Variant } from '../../models/Product';
import { getShopifyID } from '../../utils/utils';
import ButtonOutlined from '../shared/Buttons/ButtonOutlined';
import StockStatusDialog from '../dialogs/StockStatusDialog';

const H2text = styled(Typography)(({ theme }) => ({
    color: 'black',
    fontWeight: '500',
    marginTop: 0,
    fontSize: '14px',
    lineHeight: 'unset',
}));

const StyledWrapper = styled(Box)(({ theme }) => ({
    borderBottom: '1px solid #e6e6e6',
    paddingBottom: '15px',
}));

export const ProductStockStatus: React.FC<{
    product: Product;
    variant: Variant;
}> = (props) => {
    const { t } = useTranslation('product', {
        keyPrefix: 'PRODUCT.STOCKS',
    });
    const { product, variant } = props;
    const { stocks, isLoading, isError, getDefaultStock } = useProductStocksSWR(
        [product]
    );
    const [defaultStock, setDefaultStock] = useState(null);
    const [stockStatusDialogOpen, setStockStatusDialogOpen] = useState(false);

    const findDefaultLocation = (locations) => {
        setDefaultStock(getDefaultStock(locations, getShopifyID(variant.id)));
    };

    const openDialog = () => {
        setStockStatusDialogOpen(true);
    };

    const closeDialog = () => {
        setStockStatusDialogOpen(false);
    };

    useEffect(() => {
        if (!isLoading && stocks.length > 0) {
            findDefaultLocation(stocks[0].locations);
        }
    }, [isLoading]);

    if (isLoading) {
        return <></>;
    }

    if (isError) {
        return <Box>{t('ERROR')}</Box>;
    }

    return (
        <>
            <StyledWrapper>
                <H2text variant="h2" gutterBottom>
                    {t('STOCK')}
                </H2text>
                {defaultStock ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Typography sx={{ color: '#999999' }}>
                            {t('IN_STOCK', {
                                location: defaultStock.location,
                                stock: defaultStock.quantity,
                            })}
                        </Typography>
                        <ButtonOutlined onClick={openDialog}>
                            {t('SHOW_STOCKS')}
                        </ButtonOutlined>
                        <StockStatusDialog
                            open={stockStatusDialogOpen}
                            stocks={stocks}
                            onClose={closeDialog}
                        />
                    </Box>
                ) : (
                    <Typography sx={{ color: '#e0b252' }}>
                        {t('NO_DATA')}
                    </Typography>
                )}
            </StyledWrapper>
        </>
    );
};
